import { Navigate, useLocation } from 'react-router-dom';
import { type ReactElement } from 'react';
import { useAuth } from '@contexts/useAuth';
import { useCanAccess } from '@permissions/useFeatures';
import type { Feature } from '@d-orca/shared/permissions';

type IProtectedRouteProps = {
  children: ReactElement;
  /** Optional role-based gate. When set and the user lacks access, redirects to `fallbackTo`. */
  feature?: Feature;
  /** Where a user without `feature` is sent. Defaults to `/` (home); the home
   *  route itself is gated, so it names somewhere else. */
  fallbackTo?: string;
};

export function ProtectedRoute({ children, feature, fallbackTo = '/' }: IProtectedRouteProps) {
  const { user, isHydrating } = useAuth();
  const location = useLocation();
  const canAccessFeature = useCanAccess(feature);
  if (isHydrating) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!canAccessFeature) return <Navigate to={fallbackTo} replace />;
  return children;
}
